import { getBlobStoreClient } from '@/backend';
import { isBlobObjectNotFoundError, type UploadTask as BlobUploadTask } from '@/backend/contracts';
import { logger } from '@/utils/logger';
import { getEffectiveUserId } from '@/features/auth/current-user-identity';
import { buildProjectDocPath, type ProjectScope } from '../../sync/project-scope';
import { enqueueArtifactFinalization } from '@/features/sync/artifact-write';

const MAX_FILE_SIZE = 200 * 1024 * 1024; // 200MB
const MAX_PROJECT_MEDIA_SIZE = 5 * 1024 * 1024; // 5MB
const PROJECT_MEDIA_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif']);
const PROJECT_MEDIA_MIME_TYPES = new Set(['image/png', 'image/jpeg', 'image/gif']);
const PROJECT_MEDIA_FORMAT_LABEL = 'PNG, JPG, JPEG, or GIF';
const PROJECT_SET_IMAGE_EXTENSIONS = new Set([...PROJECT_MEDIA_EXTENSIONS, 'webp']);
const PROJECT_SET_IMAGE_MIME_TYPES = new Set([...PROJECT_MEDIA_MIME_TYPES, 'image/webp']);
const PROJECT_SET_IMAGE_FORMAT_LABEL = 'PNG, JPG, JPEG, GIF, or WebP';
const PROJECT_HEADSHOT_BASENAME = 'headshot';
const PROJECT_SET_IMAGE_SOURCE_REVISION_PARAM = 'v';
const PROJECT_SET_IMAGE_SOURCE_REVISION_PATTERN = /^[A-Za-z0-9._~-]+$/;

export const PROJECT_MEDIA_FILE_ACCEPT = 'image/png,image/jpeg,image/gif';

export type ProjectMediaAssetKind = 'production-logo' | 'profile-headshot';
export type ProjectSetImageKind = 'baseImage' | 'floorplanImage' | 'bakedImageUrl';

const PROJECT_SET_IMAGE_KINDS = new Set<ProjectSetImageKind>(['baseImage', 'floorplanImage', 'bakedImageUrl']);
const inFlightProjectSetImageUploads = new Map<string, BlobUploadTask>();

class ProjectSetImageUploadSupersededError extends Error {
    constructor() {
        super('Set image upload was superseded by a newer upload.');
        this.name = 'ProjectSetImageUploadSupersededError';
    }
}

function toError(error: unknown): Error {
    return error instanceof Error ? error : new Error(String(error));
}

export interface UploadProgress {
    bytesTransferred: number;
    totalBytes: number;
    percent: number;
}

export interface UploadResult {
    url?: string;
    storagePath: string;
    size: number;
    mimeType: string;
    fileId?: string; // Optional - only set for script imports
}

function uploadProgress(bytesTransferred: number, totalBytes: number): UploadProgress {
    return {
        bytesTransferred,
        totalBytes,
        percent: totalBytes === 0 ? 0 : (bytesTransferred / totalBytes) * 100,
    };
}

function projectMediaExtension(file: File): string {
    return file.name.split('.').pop()?.toLowerCase() || '';
}

function projectMediaMimeTypeForExtension(extension: string): string | null {
    if (extension === 'png') return 'image/png';
    if (extension === 'jpg' || extension === 'jpeg') return 'image/jpeg';
    if (extension === 'gif') return 'image/gif';
    if (extension === 'webp') return 'image/webp';
    return null;
}

function projectMediaStoragePath(
    scope: ProjectScope,
    kind: ProjectMediaAssetKind,
    userId: string,
    extension: string
): string {
    const projectPrefix = `${buildProjectDocPath(scope)}/media`;
    if (kind === 'production-logo') {
        return `${projectPrefix}/production-logo/logo.${extension}`;
    }

    return `${projectPrefix}/profile-headshots/${userId}/headshot.${extension}`;
}

function safeProjectMediaSegment(value: string, fieldName: string): string {
    const trimmed = value.trim();
    if (!trimmed || trimmed !== value || trimmed.includes('/') || trimmed.includes('\\') || trimmed === '.' || trimmed === '..') {
        throw new Error(`${fieldName} is not a valid project media path segment.`);
    }
    return trimmed;
}

function projectSetImageStoragePath(
    scope: ProjectScope,
    setId: string,
    imageKind: ProjectSetImageKind,
    extension: string
): string {
    if (!PROJECT_SET_IMAGE_KINDS.has(imageKind)) {
        throw new Error('Set image kind is not supported.');
    }

    return `${buildProjectDocPath(scope)}/media/sets/${safeProjectMediaSegment(setId, 'setId')}/${imageKind}.${extension}`;
}

function projectSetImageSupersedeKey(scope: ProjectScope, setId: string, imageKind: ProjectSetImageKind): string {
    return `${buildProjectDocPath(scope)}/media/sets/${safeProjectMediaSegment(setId, 'setId')}/${imageKind}`;
}

function projectSetImageSourceRevision(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function withProjectSetImageSourceRevision(storagePath: string): string {
    return `${storagePath}?${PROJECT_SET_IMAGE_SOURCE_REVISION_PARAM}=${projectSetImageSourceRevision()}`;
}

function projectSetImageObjectPath(storagePath: string | null | undefined): string | null {
    const normalizedPath = storagePath?.trim() ?? '';
    if (
        !normalizedPath
        || normalizedPath !== storagePath
        || normalizedPath.startsWith('/')
        || normalizedPath.includes('\\')
        || normalizedPath.split('/').includes('..')
    ) {
        return null;
    }

    const [objectPath, revisionQuery, extraQuery] = normalizedPath.split('?');
    if (extraQuery !== undefined || !objectPath) {
        return null;
    }

    if (revisionQuery !== undefined) {
        const [param, revision, extraParam] = revisionQuery.split('=');
        if (
            extraParam !== undefined
            || param !== PROJECT_SET_IMAGE_SOURCE_REVISION_PARAM
            || !revision
            || !PROJECT_SET_IMAGE_SOURCE_REVISION_PATTERN.test(revision)
        ) {
            return null;
        }
    }

    return objectPath;
}

function validateProjectImageFile(
    file: File,
    extensions: Set<string>,
    mimeTypes: Set<string>,
    formatLabel: string
): string | null {
    if (file.size > MAX_PROJECT_MEDIA_SIZE) {
        return `Project media must be under 5MB. This file is ${(file.size / 1024 / 1024).toFixed(1)}MB.`;
    }

    const extension = projectMediaExtension(file);
    const inferredMimeType = projectMediaMimeTypeForExtension(extension);
    const canInferMimeType = file.type === '' || file.type === 'image/*';
    const mimeType = mimeTypes.has(file.type)
        ? file.type
        : canInferMimeType
            ? inferredMimeType
            : null;

    if (!file.type.startsWith('image/') && !mimeType) {
        return 'Project media must be an image file.';
    }

    if (!mimeType || !mimeTypes.has(mimeType)) {
        return `Project media must be a ${formatLabel} image.`;
    }

    if (!extensions.has(extension)) {
        return `Project media must be a ${formatLabel} image.`;
    }

    return null;
}

type ProjectImageFileDetails = {
    extension: string;
    contentType: string;
};

function projectImageFileDetails(
    file: File,
    extensions: Set<string>,
    mimeTypes: Set<string>,
    formatLabel: string
): ProjectImageFileDetails {
    const validationError = validateProjectImageFile(
        file,
        extensions,
        mimeTypes,
        formatLabel
    );
    if (validationError) {
        throw new Error(validationError);
    }

    const extension = projectMediaExtension(file);
    const inferredMimeType = projectMediaMimeTypeForExtension(extension);
    const canInferMimeType = file.type === '' || file.type === 'image/*';
    return {
        extension,
        contentType: mimeTypes.has(file.type)
            ? file.type
            : canInferMimeType
                ? inferredMimeType ?? file.type
                : file.type,
    };
}

function validateProjectMediaImage(file: File): ProjectImageFileDetails {
    return projectImageFileDetails(
        file,
        PROJECT_MEDIA_EXTENSIONS,
        PROJECT_MEDIA_MIME_TYPES,
        PROJECT_MEDIA_FORMAT_LABEL
    );
}

function validateProjectSetImage(file: File): ProjectImageFileDetails {
    return projectImageFileDetails(
        file,
        PROJECT_SET_IMAGE_EXTENSIONS,
        PROJECT_SET_IMAGE_MIME_TYPES,
        PROJECT_SET_IMAGE_FORMAT_LABEL
    );
}

type ProjectMediaUploadInput = {
    file: File;
    storagePath: string;
    contentType: string;
    customMetadata: Record<string, string>;
    includeUrl?: boolean;
    errorLabel: string;
    onProgress?: (progress: UploadProgress) => void;
    supersedeKey?: string;
};

function uploadProjectMediaBytes(input: ProjectMediaUploadInput): Promise<UploadResult> {
    const blobStore = getBlobStoreClient();

    return new Promise((resolve, reject) => {
        const previousUploadTask = input.supersedeKey
            ? inFlightProjectSetImageUploads.get(input.supersedeKey)
            : undefined;
        const uploadTask = blobStore.upload({
            id: input.storagePath,
            storagePath: input.storagePath,
            contentType: input.contentType,
            customMetadata: input.customMetadata,
        }, input.file);

        if (input.supersedeKey) {
            inFlightProjectSetImageUploads.set(input.supersedeKey, uploadTask);
            previousUploadTask?.cancel();
        }

        const clearSupersedeKey = () => {
            if (input.supersedeKey && inFlightProjectSetImageUploads.get(input.supersedeKey) === uploadTask) {
                inFlightProjectSetImageUploads.delete(input.supersedeKey);
            }
        };
        const activeSupersedeTask = () => input.supersedeKey
            ? inFlightProjectSetImageUploads.get(input.supersedeKey)
            : undefined;
        const isSupersededTask = () => Boolean(
            input.supersedeKey && activeSupersedeTask() !== uploadTask
        );

        const unsubscribe = uploadTask.onProgress((snapshot) => {
            input.onProgress?.(uploadProgress(snapshot.bytesTransferred, snapshot.totalBytes));
        });

        void uploadTask.finished.then(
            () => {
                unsubscribe();
                const wasSuperseded = isSupersededTask();
                clearSupersedeKey();
                if (wasSuperseded) {
                    reject(new ProjectSetImageUploadSupersededError());
                    return;
                }
                void (async () => {
                    try {
                        const url = input.includeUrl
                            ? await blobStore.getDownloadUrl({ storagePath: input.storagePath })
                            : undefined;
                        resolve({
                            ...(url ? { url } : {}),
                            storagePath: input.storagePath,
                            size: input.file.size,
                            mimeType: input.contentType
                        });
                    } catch (error: unknown) {
                        reject(toError(error));
                    }
                })();
            },
            (error: unknown) => {
                unsubscribe();
                const wasSuperseded = isSupersededTask();
                clearSupersedeKey();
                if (wasSuperseded) {
                    reject(new ProjectSetImageUploadSupersededError());
                    return;
                }
                logger.error(`${input.errorLabel} upload failed:`, error);
                reject(toError(error));
            }
        );
    });
}

export function isProjectSetImageUploadSupersededError(error: unknown): boolean {
    return error instanceof ProjectSetImageUploadSupersededError;
}

function projectProfileHeadshotStoragePrefix(scope: ProjectScope): string {
    return `${buildProjectDocPath(scope)}/media/profile-headshots/`;
}

export function isProjectProfileHeadshotStoragePath(scope: ProjectScope, storagePath: string | null | undefined): storagePath is string {
    const normalizedPath = storagePath?.trim() ?? '';
    if (
        !normalizedPath
        || normalizedPath !== storagePath
        || normalizedPath.startsWith('/')
        || normalizedPath.includes('\\')
        || normalizedPath.split('/').includes('..')
    ) {
        return false;
    }

    const prefix = projectProfileHeadshotStoragePrefix(scope);
    if (!normalizedPath.startsWith(prefix)) {
        return false;
    }

    const parts = normalizedPath.slice(prefix.length).split('/');
    if (parts.length !== 2 || !parts[0] || !parts[1]) {
        return false;
    }

    const filenameParts = parts[1].split('.');
    if (filenameParts.length !== 2) {
        return false;
    }

    const [fileBase, extension] = filenameParts;
    return fileBase === PROJECT_HEADSHOT_BASENAME
        && typeof extension === 'string'
        && PROJECT_MEDIA_EXTENSIONS.has(extension.toLowerCase());
}

export function getProjectSetImageValidationError(file: File): string | null {
    return validateProjectImageFile(
        file,
        PROJECT_SET_IMAGE_EXTENSIONS,
        PROJECT_SET_IMAGE_MIME_TYPES,
        PROJECT_SET_IMAGE_FORMAT_LABEL
    );
}

export function isProjectSetImageStoragePath(storagePath: string | null | undefined): storagePath is string {
    const objectPath = projectSetImageObjectPath(storagePath);
    if (!objectPath) {
        return false;
    }

    const parts = objectPath.split('/');
    if (
        parts.length !== 8
        || parts[0] !== 'organizations'
        || !parts[1]
        || parts[2] !== 'projects'
        || !parts[3]
        || parts[4] !== 'media'
        || parts[5] !== 'sets'
        || !parts[6]
    ) {
        return false;
    }

    const filenameParts = parts[7]?.split('.') ?? [];
    if (filenameParts.length !== 2) {
        return false;
    }

    const [imageKind, extension] = filenameParts as [string, string];
    return PROJECT_SET_IMAGE_KINDS.has(imageKind as ProjectSetImageKind)
        && PROJECT_SET_IMAGE_EXTENSIONS.has(extension.toLowerCase());
}

export async function uploadProjectMediaAsset(
    file: File,
    scope: ProjectScope,
    kind: ProjectMediaAssetKind,
    onProgress?: (progress: UploadProgress) => void
): Promise<UploadResult> {
    const userId = getEffectiveUserId();
    if (!userId) throw new Error('User must be signed in to upload project media.');

    const { extension, contentType } = validateProjectMediaImage(file);
    const storagePath = projectMediaStoragePath(scope, kind, userId, extension);

    return uploadProjectMediaBytes({
        file,
        storagePath,
        contentType,
        customMetadata: {
            uploadedBy: userId,
            originalName: file.name,
            mediaKind: kind,
        },
        includeUrl: kind === 'production-logo',
        errorLabel: 'Project media',
        onProgress,
    });
}

export async function uploadProjectSetImage(
    file: File,
    scope: ProjectScope,
    setId: string,
    imageKind: ProjectSetImageKind,
    onProgress?: (progress: UploadProgress) => void
): Promise<UploadResult> {
    const userId = getEffectiveUserId();
    if (!userId) throw new Error('User must be signed in to upload project media.');

    const { extension, contentType } = validateProjectSetImage(file);
    const storagePath = projectSetImageStoragePath(scope, setId, imageKind, extension);
    const result = await uploadProjectMediaBytes({
        file,
        storagePath,
        contentType,
        customMetadata: {
            uploadedBy: userId,
            originalName: file.name,
            mediaKind: 'set-image',
            setId,
            imageKind,
        },
        errorLabel: 'Project set image',
        onProgress,
        supersedeKey: projectSetImageSupersedeKey(scope, setId, imageKind),
    });

    return {
        ...result,
        storagePath: withProjectSetImageSourceRevision(result.storagePath),
    };
}

export async function getProjectSetImageBlob(storagePath: string): Promise<Blob> {
    const objectPath = projectSetImageObjectPath(storagePath);
    if (!objectPath || !isProjectSetImageStoragePath(storagePath)) {
        throw new Error('Set image storage path is not supported.');
    }

    return getBlobStoreClient().getBlob({ storagePath: objectPath });
}

type DeleteFileFromStorageOptions = {
    warnIfNotFound?: boolean;
};

function projectSetImageObjectPathsForKind(scope: ProjectScope, setId: string, imageKind: ProjectSetImageKind): string[] {
    return [...PROJECT_SET_IMAGE_EXTENSIONS].map(extension =>
        projectSetImageStoragePath(scope, setId, imageKind, extension)
    );
}

function projectSetImageKindForObjectPath(
    scope: ProjectScope,
    setId: string,
    objectPath: string
): ProjectSetImageKind | null {
    return [...PROJECT_SET_IMAGE_KINDS].find(imageKind =>
        projectSetImageObjectPathsForKind(scope, setId, imageKind).includes(objectPath)
    ) ?? null;
}

export async function deleteProjectSetImages(scope: ProjectScope, setId: string): Promise<void> {
    const deleteTasks = [...PROJECT_SET_IMAGE_KINDS].flatMap(imageKind =>
        projectSetImageObjectPathsForKind(scope, setId, imageKind).map(storagePath =>
            deleteFileFromStorage(storagePath, { warnIfNotFound: false })
        )
    );

    await Promise.all(deleteTasks);
}

export async function deleteProjectSetImage(storagePath: string): Promise<void> {
    const objectPath = projectSetImageObjectPath(storagePath);
    if (!objectPath || !isProjectSetImageStoragePath(storagePath)) {
        throw new Error('Set image storage path is not supported.');
    }

    await deleteFileFromStorage(objectPath);
}

export async function deleteProjectSetImagePaths(
    scope: ProjectScope,
    setId: string,
    storagePaths: readonly string[]
): Promise<number> {
    const uniqueObjectPaths = new Set<string>();

    for (const storagePath of storagePaths) {
        const objectPath = projectSetImageObjectPath(storagePath);
        const imageKind = objectPath
            ? projectSetImageKindForObjectPath(scope, setId, objectPath)
            : null;
        if (imageKind) {
            for (const variantPath of projectSetImageObjectPathsForKind(scope, setId, imageKind)) {
                uniqueObjectPaths.add(variantPath);
            }
        }
    }

    await Promise.all([...uniqueObjectPaths].map(storagePath =>
        deleteFileFromStorage(storagePath, { warnIfNotFound: false })
    ));
    return uniqueObjectPaths.size;
}

/**
 * Validates file size against the 200MB limit
 */
function validateFileSize(file: File): string | null {
    if (file.size > MAX_FILE_SIZE) {
        return `File size exceeds 200MB limit. This file is ${(file.size / 1024 / 1024).toFixed(1)}MB.`;
    }
    return null;
}

/**
 * Uploads a file to project blob storage with versioning support
 */
export async function uploadFile(
    file: File,
    scope: ProjectScope,
    fileId: string,
    versionNumber: number,
    onProgress?: (progress: UploadProgress) => void
): Promise<UploadResult> {
    const userId = getEffectiveUserId();
    if (!userId) throw new Error('User must be signed in to upload files.');

    const sizeError = validateFileSize(file);
    if (sizeError) throw new Error(sizeError);

    const storagePath = `${buildProjectDocPath(scope)}/files/${fileId}/v${versionNumber}/${file.name}`;
    const uploadTask = getBlobStoreClient().upload(
        {
            id: `file-version-${fileId}-v${versionNumber}`,
            storagePath,
            contentType: file.type,
            customMetadata: {
                uploadedBy: userId,
                originalName: file.name,
                fileId,
                versionNumber: versionNumber.toString(),
            },
        },
        file,
    );
    const unsubscribe = uploadTask.onProgress((snapshot) => {
        onProgress?.(uploadProgress(snapshot.bytesTransferred, snapshot.totalBytes));
    });

    try {
        await uploadTask.finished;
    } catch (error: unknown) {
        logger.error('Upload failed:', error);
        throw toError(error);
    } finally {
        unsubscribe();
    }

    await enqueueArtifactFinalization({
        scope,
        actorId: userId,
        feature: 'files',
        capability: 'CREATE_FILES',
        conflictUnit: `files.artifacts/${fileId}`,
        artifactType: 'fileVersion',
        entityId: fileId,
        storagePath,
        contentType: file.type,
        metadata: {
            fileName: file.name,
            mimeType: file.type,
            size: file.size,
            versionNumber,
        },
    });

    return {
        storagePath,
        size: file.size,
        mimeType: file.type
    };
}

/**
 * Deletes a file from blob storage
 */
export async function deleteFileFromStorage(
    storagePath: string,
    options: DeleteFileFromStorageOptions = {}
): Promise<void> {
    const ignoreMissing = options.warnIfNotFound === false;
    try {
        await getBlobStoreClient().cleanup({ storagePath, ignoreMissing });
        if (!ignoreMissing) {
            logger.files(`Deleted file from storage: ${storagePath}`);
        }
    } catch (error: unknown) {
        // Ignore errors if file doesn't exist
        if (isBlobObjectNotFoundError(error)) {
            if (options.warnIfNotFound !== false) {
                logger.warn(`File not found in storage: ${storagePath}`);
            }
            return;
        }
        throw error;
    }
}

/**
 * Uploads an organization logo to blob storage.
 * Path: organizations/{orgId}/logo/logo.{ext}
 * Does NOT require a project to be open.
 */
export async function uploadOrgLogo(
    file: File,
    orgId: string,
    onProgress?: (progress: UploadProgress) => void
): Promise<UploadResult> {
    const userId = getEffectiveUserId();
    if (!userId) throw new Error('User must be signed in to upload a logo.');

    const MAX_LOGO_SIZE = 5 * 1024 * 1024; // 5MB
    if (file.size > MAX_LOGO_SIZE) {
        throw new Error(`Logo must be under 5MB. This file is ${(file.size / 1024 / 1024).toFixed(1)}MB.`);
    }

    if (!file.type.startsWith('image/')) {
        throw new Error('Logo must be an image file.');
    }

    const ext = file.name.split('.').pop() ?? 'png';
    const storagePath = `organizations/${orgId}/logo/logo.${ext}`;
    const blobStore = getBlobStoreClient();
    const uploadTask = blobStore.upload(
        {
            id: storagePath,
            storagePath,
            contentType: file.type,
            customMetadata: {
                uploadedBy: userId,
                originalName: file.name,
            },
        },
        file,
    );
    const unsubscribe = uploadTask.onProgress((snapshot) => {
        onProgress?.(uploadProgress(snapshot.bytesTransferred, snapshot.totalBytes));
    });

    try {
        await uploadTask.finished;
    } catch (error: unknown) {
        logger.error('Org logo upload failed:', error);
        throw toError(error);
    } finally {
        unsubscribe();
    }

    const downloadUrl = await blobStore.getDownloadUrl({ storagePath });
    return {
        url: downloadUrl,
        storagePath,
        size: file.size,
        mimeType: file.type
    };
}
